import { httpsCallable } from "firebase/functions";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  type QueryDocumentSnapshot,
} from "firebase/firestore";

import { db, functionsClient } from "./firebase";
import { mapPublicAccount, type PublicProfileRecord } from "./publicProfiles";

const DEFAULT_PAGE_SIZE = 20;
const MAX_PAGE_SIZE = 50;

type FollowMutationResult = {
  following: boolean;
  changed: boolean;
  followersCount: number;
  followingCount: number;
};

export type AccountConnectionDirection = "followers" | "following";

export type AccountConnectionPage = {
  accounts: PublicProfileRecord[];
  cursor: QueryDocumentSnapshot | null;
  hasMore: boolean;
};

const followAccountCallable = httpsCallable<
  { followerAccountId: string; targetAccountId: string },
  FollowMutationResult
>(functionsClient, "followAccount");

const unfollowAccountCallable = httpsCallable<
  { followerAccountId: string; targetAccountId: string },
  FollowMutationResult
>(functionsClient, "unfollowAccount");

function cleanAccountId(accountId: string | null | undefined) {
  return String(accountId ?? "").trim();
}

function validatePair(followerAccountId: string, targetAccountId: string) {
  const follower = cleanAccountId(followerAccountId);
  const target = cleanAccountId(targetAccountId);
  if (!follower || !target || follower.includes("/") || target.includes("/")) {
    throw new Error("Identité de suivi invalide.");
  }
  if (follower === target) throw new Error("Une identité ne peut pas se suivre elle-même.");
  return { follower, target };
}

export async function followIdentity(followerAccountId: string, targetAccountId: string) {
  const { follower, target } = validatePair(followerAccountId, targetAccountId);
  const result = await followAccountCallable({
    followerAccountId: follower,
    targetAccountId: target,
  });
  return result.data;
}

export async function unfollowIdentity(followerAccountId: string, targetAccountId: string) {
  const { follower, target } = validatePair(followerAccountId, targetAccountId);
  const result = await unfollowAccountCallable({
    followerAccountId: follower,
    targetAccountId: target,
  });
  return result.data;
}

export async function fetchFollowingIds(followerAccountId: string, max = 1000) {
  const follower = cleanAccountId(followerAccountId);
  if (!follower || max <= 0) return [];

  const snapshot = await getDocs(query(
    collection(db, "accounts", follower, "following"),
    orderBy("createdAt", "desc"),
    limit(Math.min(Math.floor(max), 1000))
  ));
  return Array.from(new Set(snapshot.docs.map((item) => item.id)));
}

export function listenFollowStatus(
  followerAccountId: string | null | undefined,
  targetAccountId: string | null | undefined,
  onChange: (following: boolean) => void,
  onError?: (error: unknown) => void
) {
  const follower = cleanAccountId(followerAccountId);
  const target = cleanAccountId(targetAccountId);

  if (!follower || !target || follower === target) {
    onChange(false);
    return () => undefined;
  }

  return onSnapshot(
    doc(db, "accounts", target, "followers", follower),
    (snapshot) => onChange(snapshot.exists()),
    (error) => onError?.(error)
  );
}

export async function fetchAccountConnectionsPage(params: {
  accountId: string;
  direction: AccountConnectionDirection;
  cursor?: QueryDocumentSnapshot | null;
  pageSize?: number;
}): Promise<AccountConnectionPage> {
  const accountId = cleanAccountId(params.accountId);
  if (!accountId || accountId.includes("/")) {
    return { accounts: [], cursor: null, hasMore: false };
  }

  const pageSize = Math.max(1, Math.min(Math.floor(params.pageSize ?? DEFAULT_PAGE_SIZE), MAX_PAGE_SIZE));
  const relationQuery = query(
    collection(db, "accounts", accountId, params.direction),
    orderBy("createdAt", "desc")
  );
  const pageQuery = params.cursor
    ? query(relationQuery, startAfter(params.cursor), limit(pageSize))
    : query(relationQuery, limit(pageSize));
  const snapshot = await getDocs(pageQuery);
  const accountIds = Array.from(new Set(snapshot.docs.map((item) => item.id)));
  const accountSnapshots = await Promise.allSettled(
    accountIds.map((id) => getDoc(doc(db, "accounts", id)))
  );
  const accounts = accountSnapshots.flatMap((result) => {
    if (result.status !== "fulfilled" || !result.value.exists()) return [];
    const data = result.value.data();
    if (data.status !== "active" || data.accountId !== result.value.id) return [];
    return [mapPublicAccount(result.value.id, data)];
  });

  return {
    accounts,
    cursor: snapshot.docs.at(-1) ?? params.cursor ?? null,
    hasMore: snapshot.size === pageSize,
  };
}
